import React from "react";
import { Expand } from "lucide-react";
import { createPortal } from "react-dom";
import "./RouteMapPanel.css";
import useRouteMapController from "../hooks/useRouteMapController.js";
import useRouteMapGroupRangePreference from "../hooks/useRouteMapGroupRangePreference.js";
import { getSelectedClientStopType } from "../utils/routeModelsData.js";
import { ObservabilityKpiSection } from "./resultsPageChrome/SupportSections.jsx";
import RouteLegend from "./RouteLegend.jsx";
import RouteStatusNotice from "./RouteStatusNotice.jsx";

export function RouteMapGroupRangeToggle({ className = "" }) {
  const {
    setShowGroupedStopRangeLabels,
    showGroupedStopRangeLabels,
  } = useRouteMapGroupRangePreference();

  return (
    <label
      className={`inline-flex min-h-7 cursor-pointer items-center gap-1.5 rounded-[4px] border border-[rgba(148,163,184,0.18)] bg-white px-2 py-1 text-[0.64rem] font-semibold text-content-copy transition hover:border-border-accent-strong hover:text-text ${className}`.trim()}
      title="Afficher la plage complète des groupes, par exemple 9-12"
    >
      <input
        type="checkbox"
        checked={showGroupedStopRangeLabels}
        onChange={(event) => {
          setShowGroupedStopRangeLabels(event.target.checked);
        }}
        className="h-3.5 w-3.5 accent-accent"
        data-route-map-group-range-toggle="true"
      />
      <span className="whitespace-nowrap">Afficher groupes</span>
    </label>
  );
}

function RouteMapPanelContent({
  compact = false,
  customMapHeight = null,
  fillAvailableHeight = false,
  floatingSize = null,
  isFloating = false,
  isExpanded = false,
  mapContainerRef,
  mapController = null,
  observabilityItems = [],
  onExpand = null,
  overlayLabel = "",
  renderGroupRangeToggle = true,
  renderLegend = true,
  renderObservability = true,
  routeDistanceLabel = "",
  routeStatus,
  statusNotice,
  stopBadgePresentation = "number",
  trip,
}) {
  const normalizedCustomMapHeight = Number(customMapHeight);
  const visibleObservabilityItems = renderObservability && Array.isArray(observabilityItems)
    ? observabilityItems.filter(Boolean)
    : [];
  const hasObservabilityOverlay = visibleObservabilityItems.length > 0;
  const shouldRenderGroupRangeToggle = stopBadgePresentation === "number" && renderGroupRangeToggle;
  const shouldRenderFooter = renderLegend || visibleObservabilityItems.length > 0;
  const fillAvailableGridRows = shouldRenderGroupRangeToggle
    ? shouldRenderFooter
      ? "grid-rows-[auto_minmax(0,1fr)_auto]"
      : "grid-rows-[auto_minmax(0,1fr)]"
    : shouldRenderFooter
      ? "grid-rows-[minmax(0,1fr)_auto]"
      : "grid-rows-[minmax(0,1fr)]";
  const customMapStyle = fillAvailableHeight
    ? {
        height: "100%",
        minHeight: "100%",
      }
    : Number.isFinite(normalizedCustomMapHeight) && normalizedCustomMapHeight > 0
    ? {
        height: `${normalizedCustomMapHeight}px`,
        minHeight: `${normalizedCustomMapHeight}px`,
      }
    : undefined;

  return (
    <div
      data-react-route-map-panel="true"
      data-route-map-expanded={isExpanded ? "true" : "false"}
      data-route-map-floating={isFloating ? "true" : undefined}
      data-route-map-floating-size={floatingSize || undefined}
      data-route-map-variant={compact ? "compact" : "standard"}
      className={fillAvailableHeight
        ? `grid h-full min-h-0 min-w-0 w-full gap-2 ${fillAvailableGridRows}`
        : "grid min-w-0 w-full max-w-full gap-2 overflow-hidden"}
    >
      {shouldRenderGroupRangeToggle ? (
        <div className="flex min-w-0 justify-end">
          <RouteMapGroupRangeToggle />
        </div>
      ) : null}

      <section className={fillAvailableHeight ? "route-stage__map-shell dispatch-map-shell h-full min-h-0 w-full min-w-0" : "route-stage__map-shell dispatch-map-shell w-full min-w-0"} style={customMapStyle}>
        <div className={`absolute left-1.5 right-1.5 top-1.5 z-20 flex items-center justify-between gap-1 ${onExpand ? "fleet-map-overlay-controls" : ""}`}>
        {overlayLabel ? (
          <span
            className="inline-flex h-6 items-center rounded-[4px] border border-[rgba(15,92,87,0.35)] bg-white px-2 text-[0.7rem] font-extrabold text-[#0f3d3a] shadow-[0_2px_6px_rgba(15,23,42,0.15)]"
            data-route-map-overlay-label="true"
          >
            {overlayLabel}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => {
            mapController?.restoreView?.();
          }}
          aria-label="Recentrer la carte"
          title="Recentrer la carte sur la tournée"
          className="mr-auto inline-flex h-6 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white px-1.5 text-[0.64rem] font-semibold text-slate-700 shadow-none transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35"
        >
          Recentrer
        </button>
        {onExpand ? (
          <div className="flex items-center gap-1" role="group" aria-label="Commandes de la carte routière">
            <RouteMapGroupRangeToggle />
            <button type="button" onClick={onExpand} aria-label="Agrandir la carte" title="Agrandir la carte" className="inline-flex items-center justify-center rounded-[4px] border border-slate-300 bg-white px-1 text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35">
              <Expand aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        </div>
        <div
          ref={mapContainerRef}
          className={fillAvailableHeight ? "route-map h-full min-h-0 max-w-full overflow-hidden" : "route-map max-w-full overflow-hidden"}
          aria-label="Carte de la tournée sélectionnée"
          style={customMapStyle}
        />
        <RouteStatusNotice notice={statusNotice} routeStatus={routeStatus} />

        {hasObservabilityOverlay ? (
          <div
            className="pointer-events-none absolute bottom-2.5 z-1000 hidden justify-end min-[841px]:flex"
            style={{
              left: "calc(var(--fleet-dock-width, 0px) + 0.75rem)",
              right: "calc(var(--route-steps-dock-width, 0px) + 0.75rem)",
            }}
          >
            <ObservabilityKpiSection
              className="pointer-events-auto w-fit max-w-none"
              dense
              inline
              items={visibleObservabilityItems}
            />
          </div>
        ) : null}
      </section>

      {shouldRenderFooter ? (
        <div className="grid min-w-0 gap-1.25">
          {renderLegend ? <RouteLegend compact={compact} stopBadgePresentation={stopBadgePresentation} trip={trip} /> : null}
          {hasObservabilityOverlay ? (
            <ObservabilityKpiSection
              className="min-w-0 hidden max-[840px]:grid"
              dense
              items={visibleObservabilityItems}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function RouteMapPanel({
  assignment = null,
  compact = false,
  customMapHeight = null,
  fillAvailableHeight = false,
  floatingSize = null,
  isFloating = false,
  isRouteViewActive = true,
  isExpanded = false,
  mountNode = null,
  observabilityItems = [],
  onExpand = null,
  overlayLabel = "",
  onClientSelect = null,
  onControllerReady = null,
  onMapRenderMetaChange = null,
  onRouteHighlightChange = null,
  renderGroupRangeToggle = true,
  renderInline = false,
  renderLegend = true,
  renderObservability = true,
  routeAlternativePreview = null,
  routeDistanceLabel = "",
  selectedClientId = null,
  selectionRequest = null,
  stopBadgePresentation = "number",
  stateRef,
  trip = null,
}) {
  const { showGroupedStopRangeLabels } = useRouteMapGroupRangePreference();
  const selectedStopType = getSelectedClientStopType(trip, selectedClientId);
  const effectiveShowGroupedStopRangeLabels = stopBadgePresentation === "number"
    ? showGroupedStopRangeLabels
    : true;
  const {
    controller,
    mapContainerRef,
    routeStatus,
    statusNotice,
  } = useRouteMapController({
    assignment,
    isRouteViewActive,
    onClientSelect,
    onControllerReady,
    onMapRenderMetaChange,
    onRouteHighlightChange,
    routeAlternativePreview,
    routeDistanceLabel,
    selectedClientId,
    selectedStopType,
    selectionRequest,
    showGroupedStopRangeLabels: effectiveShowGroupedStopRangeLabels,
    stopBadgePresentation,
    stateRef,
    trip,
  });

  const content = (
    <RouteMapPanelContent
      compact={compact}
      customMapHeight={customMapHeight}
      fillAvailableHeight={fillAvailableHeight}
      floatingSize={floatingSize}
      isFloating={isFloating}
      isExpanded={isExpanded}
      mapContainerRef={mapContainerRef}
      mapController={controller}
      observabilityItems={observabilityItems}
      onExpand={onExpand}
      overlayLabel={overlayLabel}
      renderGroupRangeToggle={renderGroupRangeToggle}
      renderLegend={renderLegend}
      renderObservability={renderObservability}
      routeDistanceLabel={routeDistanceLabel}
      routeStatus={routeStatus}
      statusNotice={statusNotice}
      stopBadgePresentation={stopBadgePresentation}
      trip={trip}
    />
  );

  if (renderInline) {
    return content;
  }

  if (!(mountNode instanceof HTMLElement)) {
    return null;
  }

  return createPortal(content, mountNode);
}
